import React, { useContext } from 'react'
import type { CanvasDropZoneBand } from './canvasCatalogDrop'

export interface CanvasDropZoneEntry {
  accepts: 'block' | 'section'
  /** `null` when the rail is unmounted or detached, so the canvas skips it. */
  getRect: () => { top: number; bottom: number } | null
  onDrop: (dataTransfer: DataTransfer) => void
}

export interface CanvasDropZoneRegistryValue {
  /** Returns its own cleanup, so a rail can register in a `useEffect` and unregister on unmount. */
  register: (id: string, entry: CanvasDropZoneEntry) => () => void
  /** The rail a drop would land in right now, or `null` outside a catalog drag. */
  activeZoneId: string | null
}

/**
 * Lets every {@link CanvasDropZone} publish its rect and its drop handler to the canvas root, which
 * owns hit-testing for catalog drags (see `pickNearestDropZone` for why).
 *
 * A context rather than props: the rails render from six separate call sites inside
 * `Presentation.tsx`, several of them nested inside `Droppable`/`Draggable` render props, so
 * threading a registry down as a prop would touch every one of them and every render callback
 * between.
 */
export const CanvasDropZoneRegistryContext = React.createContext<CanvasDropZoneRegistryValue | null>(null)

export function useCanvasDropZoneRegistry(): CanvasDropZoneRegistryValue | null {
  return useContext(CanvasDropZoneRegistryContext)
}

/** Reads the live rect of every registered rail, dropping any that has since detached. */
export function readCanvasDropZoneBands(zones: Map<string, CanvasDropZoneEntry>): CanvasDropZoneBand[] {
  const bands: CanvasDropZoneBand[] = []

  zones.forEach((entry, id) => {
    const rect = entry.getRect()
    if (!rect) {
      return
    }

    bands.push({ id, accepts: entry.accepts, top: rect.top, bottom: rect.bottom })
  })

  return bands
}
